<script setup lang="ts">
import { useAuthStore } from '~/store/authStore'
import { getGameStore } from '~/store/localGameStore'
import refreshAll from '~/services/refreshHelper'

const localePath = useLocalePath()
const { t } = useI18n()

const userStore = useAuthStore()
const user = await userStore.getUser()

const route = useRoute()

const { maximize } = inject<{ maximize: Ref<boolean>, toggleMaximize: () => void }>('toggleMaximize')!

watch(() => route.path, () => {
  maximize.value = false
})

const focused = useWindowFocus()
const lastRefresh = ref<number>(Date.now())
whenever(focused, () => {
  const now = Date.now()
  if (now - lastRefresh.value > 60000) {
    refreshAll(() => {})
    lastRefresh.value = now
  }
})

const connectedUsers = ref<User[]>([])

watch(connectedUsers, (newValue) => {
  const printerUser = newValue.find(printer_user => printer_user.id.toString() == `printer_${user?.id}`)
  if (printerUser) {
    userStore.isPrinterConnected = true
  }
  else {
    userStore.isPrinterConnected = false
  }
}, { deep: true })

onMounted(() => {
  if (user) {
    window.Echo.join(`user.${user.id}`)
      .here((users: User[]) => {
        connectedUsers.value = users
      })
      .joining((user: User) => {
        connectedUsers.value.push(user)
      })
      .leaving((user: User) => {
        connectedUsers.value = connectedUsers.value.filter(u => u.id !== user.id)
      })

    window.Echo.private('user.' + user.id)
      .listen('.game-ready', (data: { game: Game }) => {
        useSonner.success(t('Game ready'), {
          description: t('Your game is ready to start'),
        })

        getGameStore(data.game.id.toString()).initFromApi(data.game)
        navigateTo(localePath(`/play/online/${data.game.id}`))
      })
      .listen('.game-cancelled', () => {
        useSonner.error(t('Game cancelled'), {
          description: t('A game you were in has been cancelled'),
        })

        userStore.getUser(true)
      })
      .notification((notification: { title: string, body: string }) => {
        useSonner.info(notification.title, {
          description: notification.body,
        })

        userStore.loadFreshNotifications()
      })
  }
})
</script>

<template>
  <UiSidebarBoardsProvider>
    <UiSidebarProvider>
      <UiSidebar
        v-if="!maximize"
        id="main"
        collapsible="icon"
      >
        <UiSidebarHeader>
          <RouterLink
            to="/"
            class="group-data-[state=collapsed]:hidden px-2"
          >
            <img
              src="/light-logo.png"
              alt="DartsSpace logo"
              class="hidden dark:!block"
            >
            <img
              src="/dark-logo.png"
              alt="DartsSpace logo"
              class="dark:hidden"
            >
          </RouterLink>
          <div class="hidden group-data-[state=collapsed]:!flex w-full items-center justify-center">
            <img
              src="/icon.svg"
              alt="DartsSpace logo"
              class="size-8"
            >
          </div>
        </UiSidebarHeader>
        <UiSidebarContent>
          <LayoutSidebarLinks />
        </UiSidebarContent>
        <LayoutSidebarFooter />
        <UiSidebarRail />
      </UiSidebar>
      <UiSidebarInset
        class="overflow-hidden peer-data-[variant=inset]:max-h-[calc(100svh-theme(spacing.4))] max-h-svh"
      >
        <LayoutNavbar v-if="!maximize" />
        <main class="flex flex-1 h-full w-full overflow-hidden relative">
          <slot />
        </main>
      </UiSidebarInset>
      <slot name="sidebar-right" />
    </UiSidebarProvider>
  </UiSidebarBoardsProvider>
</template>
